import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { PromiseStatuses } from '../types';
import { DatabaseSection } from '../utils';
import { ContactType, DatabaseResponse, FindAllContactsFilters, FindContactsDatabaseResponse, SectionDatabaseContacts } from './dto';
import { NewContactsListService } from './service';

const controllers: Record<string, AbortController> = {};

const emptyResponsePerson: DatabaseResponse<ContactType.PERSON> = {
    response: {
        data: [],
        total: 0,
        page: 0,
        perPage: 25,
        prev: false,
        next: false,
        totalPage: 0
    },
    status: 'idle'
}

const emptyResponseCompany: DatabaseResponse<ContactType.COMPANY> = {
    response: {
        data: [],
        total: 0,
        page: 0,
        perPage: 25,
        prev: false,
        next: false,
        totalPage: 0
    },
    status: 'idle'
}

const initialState: SectionDatabaseContacts = {
    database: {
        PERSON: emptyResponsePerson,
        COMPANY: emptyResponseCompany
    },
    "add-target-db": {
        PERSON: emptyResponsePerson,
        COMPANY: emptyResponseCompany
    },
    "black-list": {
        PERSON: emptyResponsePerson,
        COMPANY: emptyResponseCompany
    },
    "verify-list": {
        PERSON: emptyResponsePerson,
        COMPANY: emptyResponseCompany
    },
};

export const findContactsDatabase = createAsyncThunk<
    { response: FindContactsDatabaseResponse<ContactType>; section: DatabaseSection; contactType: ContactType },
    { filters: FindAllContactsFilters; section: DatabaseSection; contactType: ContactType },
    { rejectValue: "aborted" }
>(
    "contacts/findContactsDatabase",
    async (request, thunkApi) => {
        const key = `${request.section}-${request.contactType}`;

        if (controllers[key]) {
            controllers[key].abort();
        }
        const controller = new AbortController();
        controllers[key] = controller;

        const ContactsService = NewContactsListService();

        try {
            const response = await ContactsService.findContactsDatabase(
                request.filters,
                request.contactType,
                controller.signal
            );

            return {
                response,
                section: request.section,
                contactType: request.contactType,
            };
        } catch (err: any) {
            if (
                axios.isCancel?.(err) ||
                err.name === "CanceledError" ||
                err.name === "AbortError"
            ) {
                return thunkApi.rejectWithValue("aborted");
            }
            throw err;
        }
    }
);

const contactsSlice = createSlice({
    name: 'databaseContacts',
    initialState,
    reducers: {
        setFindContactsStatus: (
            state,
            action: PayloadAction<{ section: DatabaseSection; status: PromiseStatuses, contactType: ContactType }>
        ) => {
            const { section, status, contactType } = action.payload;

            state[section] = {
                ...state[section],
                [contactType]: {
                    response: state[section][contactType].response,
                    status,
                }
            };
        },
    },
    extraReducers(builder) {
        builder.addCase(findContactsDatabase.fulfilled, (state, action) => {
            const { section, response, contactType } = action.payload;
            state[section] = {
                ...state[section],
                [contactType]: {
                    response,
                    status: 'successfully',
                }
            };
        });

        builder.addCase(findContactsDatabase.pending, (state, action) => {
            const { section, contactType } = action.meta.arg;

            state[section] = {
                ...state[section],
                [contactType]: {
                    response: {
                        data: [],
                        total: 0,
                        page: 0,
                        perPage: 25,
                        prev: false,
                        next: false,
                        totalPage: 0
                    },
                    status: 'loading',
                }
            };
        });

        builder.addCase(findContactsDatabase.rejected, (state, action) => {
            if (action.payload === "aborted") {
                return;
            }
            
            const { section, contactType } = action.meta.arg;

            state[section] = {
                ...state[section],
                [contactType]: {
                    response: {
                        data: [],
                        total: 0,
                        page: 0,
                        perPage: 25,
                        prev: false,
                        next: false,
                        totalPage: 0
                    },
                    status: "failed",
                }
            };
        });
    },
});

export const { setFindContactsStatus } = contactsSlice.actions;
export default contactsSlice.reducer;
